import { create } from 'zustand';

interface MessageUnreadState {
  count: number;
  /** Conversation ids currently known to contain one or more unread messages. */
  unreadConversationIds: Record<number, true>;
  /** Locally opened chats awaiting the server unread count to reach zero. */
  locallyClearedConversationIds: Record<number, true>;
  /** Live unread messages received while the Messages route is not mounted. */
  pendingByConversation: Record<number, number>;
  setCount: (count: number) => void;
  increment: () => void;
  incrementConversation: (conversationId: number) => void;
  clearConversation: (conversationId: number) => void;
  syncUnreadConversations: (
    conversationIds: readonly number[],
    acknowledgeServerClears?: boolean,
  ) => void;
  /** Remove every account-scoped unread value during logout/account change. */
  reset: () => void;
}

const EMPTY_UNREAD_STATE = {
  count: 0,
  unreadConversationIds: {},
  locallyClearedConversationIds: {},
  pendingByConversation: {},
} as const;

export const useMessageUnreadStore = create<MessageUnreadState>((set) => ({
  ...EMPTY_UNREAD_STATE,
  setCount: (count) => set({ count: Math.max(0, Math.trunc(Number(count) || 0)) }),
  increment: () => set((state) => ({ count: state.count + 1 })),
  incrementConversation: (conversationId) =>
    set((state) => {
      if (!Number.isFinite(conversationId) || conversationId <= 0) return state;
      const alreadyUnread = Boolean(state.unreadConversationIds[conversationId]);
      const locallyClearedConversationIds = { ...state.locallyClearedConversationIds };
      delete locallyClearedConversationIds[conversationId];
      return {
        count: alreadyUnread ? state.count : state.count + 1,
        unreadConversationIds: {
          ...state.unreadConversationIds,
          [conversationId]: true,
        },
        locallyClearedConversationIds,
        pendingByConversation: {
          ...state.pendingByConversation,
          [conversationId]: (state.pendingByConversation[conversationId] ?? 0) + 1,
        },
      };
    }),
  clearConversation: (conversationId) =>
    set((state) => {
      const pending = state.pendingByConversation[conversationId] ?? 0;
      if (pending === 0 && !state.unreadConversationIds[conversationId]) return state;
      const pendingByConversation = { ...state.pendingByConversation };
      const unreadConversationIds = { ...state.unreadConversationIds };
      const locallyClearedConversationIds = {
        ...state.locallyClearedConversationIds,
        [conversationId]: true as const,
      };
      delete pendingByConversation[conversationId];
      delete unreadConversationIds[conversationId];
      return {
        count: Object.keys(unreadConversationIds).length,
        pendingByConversation,
        unreadConversationIds,
        locallyClearedConversationIds,
      };
    }),
  syncUnreadConversations: (conversationIds, acknowledgeServerClears = false) =>
    set((state) => {
      const unreadConversationIds: Record<number, true> = {};
      const incomingIds = new Set(conversationIds);
      const locallyClearedConversationIds: Record<number, true> = {};
      for (const rawId of Object.keys(state.locallyClearedConversationIds)) {
        const id = Number(rawId);
        // Keep suppressing a stale positive REST value. Once the server drops
        // the id, the acknowledgement is complete and suppression can end.
        if (incomingIds.has(id) || !acknowledgeServerClears) {
          locallyClearedConversationIds[id] = true;
        }
      }
      for (const id of conversationIds) {
        if (Number.isFinite(id) && id > 0 && !locallyClearedConversationIds[id]) {
          unreadConversationIds[id] = true;
        }
      }
      // Preserve live XMPP arrivals that the eventually-consistent REST
      // endpoint has not returned yet.
      for (const id of Object.keys(state.pendingByConversation).map(Number)) {
        if (id > 0) unreadConversationIds[id] = true;
      }
      return {
        unreadConversationIds,
        locallyClearedConversationIds,
        count: Object.keys(unreadConversationIds).length,
      };
    }),
  reset: () =>
    set({
      count: 0,
      unreadConversationIds: {},
      locallyClearedConversationIds: {},
      pendingByConversation: {},
    }),
}));
